import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, FileText, Layers, QrCode, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, Loading, Surface } from "@/components/ds";
import { PixChargeModal } from "@/components/orders/PixChargeModal";

const STATUS = {
  AGUARDANDO_PAGAMENTO: { label: "Aguardando pagamento", cls: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  PAGAMENTO_INFORMADO: { label: "Pagamento informado", cls: "border-brand/40 bg-brand/15 text-brand" },
  PAGO: { label: "Pago", cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
};

/** Fechamentos de consignação de todos os parceiros, com confirmação de pagamento. */
export function AdminClosures() {
  const pdf = async (c) => {
    try {
      await openPdf(`/closures/${c.id}/pdf`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const [list, setList] = useState(null);
  const [pixFor, setPixFor] = useState(null);

  const chargePix = async (c) => {
    try {
      const { data } = await api.post(`/closures/${c.id}/pix-charge`);
      setPixFor(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const load = useCallback(() => {
    api
      .get("/closures")
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(load, [load]);

  const confirm = async (c) => {
    try {
      await api.put(`/closures/${c.id}/confirm-payment`);
      toast.success(`Pagamento de ${c.partner_name} confirmado.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (list === null) return <Loading label="Carregando fechamentos..." />;
  if (list.length === 0)
    return (
      <EmptyState
        icon={Wallet}
        title="Nenhum fechamento gerado"
        description="Os parceiros geram o fechamento do período na área deles, sempre a partir do dia 10."
      />
    );

  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="admin-closures-list">
      {list.map((c) => (
        <Surface key={c.id} className="min-w-0 overflow-hidden p-5" data-testid={`admin-closure-card-${c.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate font-display text-base font-medium">{c.partner_name}</p>
              <p className="text-xs text-muted-foreground">
                {c.number} · {c.period_label}
              </p>
            </div>
            <span
              className={`shrink-0 rounded-full border px-2.5 py-1 text-xs ${STATUS[c.status]?.cls}`}
              data-testid={`admin-closure-status-${c.id}`}
            >
              {STATUS[c.status]?.label || c.status}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
            <div className="min-w-0">
              <p className="truncate text-[11px] text-muted-foreground">Vendas</p>
              <p className="font-num">{c.sold} un</p>
            </div>
            <div className="min-w-0">
              <p className="truncate text-[11px] text-muted-foreground">Estoque final</p>
              <p className="font-num">{c.stock_final} un</p>
            </div>
            <div className="min-w-0">
              <p className="truncate text-[11px] text-muted-foreground">Valor devido</p>
              <p className="font-num text-brand" data-testid={`admin-closure-amount-${c.id}`}>
                {brl(c.amount_due)}
              </p>
            </div>
          </div>
          {c.payment && (
            <p className="mt-3 rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs text-muted-foreground">
              Pagamento informado em {dataBR(c.payment.informed_at)}
              {c.payment.notes ? ` · ${c.payment.notes}` : ""}
            </p>
          )}
          {c.status !== "PAGO" ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => confirm(c)}
                data-testid={`confirm-payment-btn-${c.id}`}
                className="h-9 gap-1.5 rounded-full text-xs font-semibold"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Confirmar pagamento
              </Button>
              {Number(c.amount_due) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => chargePix(c)}
                  data-testid={`closure-charge-pix-btn-${c.id}`}
                  className="h-9 gap-1.5 rounded-full text-xs"
                >
                  <QrCode className="h-3.5 w-3.5" /> Cobrar Pix
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => pdf(c)}
                data-testid={`admin-closure-pdf-btn-${c.id}`}
                className="h-9 gap-1.5 rounded-full text-xs"
              >
                <FileText className="h-3.5 w-3.5" /> Gerar PDF
              </Button>
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => pdf(c)}
                data-testid={`admin-closure-pdf-btn-${c.id}`}
                className="h-9 gap-1.5 rounded-full text-xs"
              >
                <FileText className="h-3.5 w-3.5" /> Gerar PDF
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Confirmado em {dataBR(c.paid_confirmed_at)} por {c.paid_confirmed_by}
              </p>
            </div>
          )}
        </Surface>
      ))}
      {pixFor && <PixChargeModal charge={pixFor} onClose={() => setPixFor(null)} />}
    </div>
  );
}

/** Onde cada unidade está: estoque TR3ND x consignado por parceiro x vendido. */
export function StockOverview() {
  const [list, setList] = useState(null);
  const [term, setTerm] = useState("");
  const [onlyConsigned, setOnlyConsigned] = useState(true);

  useEffect(() => {
    api
      .get("/stock/overview")
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (list === null) return <Loading label="Carregando estoque..." />;
  if (list.length === 0)
    return <EmptyState icon={Layers} title="Sem estoque registrado" description="Cadastre produtos e registre entradas." />;

  const rows = list.filter((r) => {
    if (onlyConsigned && !r.consigned_units && !r.sold_by_partners) return false;
    const q = term.trim().toLowerCase();
    return !q || r.name.toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3" data-testid="stock-overview-list">
      <Surface className="flex flex-wrap items-center gap-3 p-4">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Buscar por nome ou código"
          className="h-10 max-w-xs"
          data-testid="overview-search-input"
        />
        <button
          type="button"
          onClick={() => setOnlyConsigned((v) => !v)}
          data-testid="overview-filter-consigned"
          className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
            onlyConsigned
              ? "border-brand/50 bg-brand/15 text-brand"
              : "border-white/10 text-muted-foreground"
          }`}
        >
          Só com consignado
        </button>
        <span className="text-xs text-muted-foreground">{rows.length} produto(s)</span>
      </Surface>
      {rows.map((row) => (
        <Surface key={row.product_id} className="min-w-0 overflow-hidden p-4" data-testid={`overview-row-${row.product_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{row.name}</p>
              <p className="font-num text-[11px] text-muted-foreground">{row.code}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-num text-sm" data-testid={`overview-total-${row.product_id}`}>
                {row.physical_total} un
              </p>
              <p className="text-[10px] text-muted-foreground">total físico</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
            <div className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-2.5">
              <p className="truncate text-[11px] text-muted-foreground">TR3ND</p>
              <p className="font-num" data-testid={`overview-tr3nd-${row.product_id}`}>
                {row.tr3nd_units} un
              </p>
            </div>
            <div className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-2.5">
              <p className="truncate text-[11px] text-muted-foreground">Consignado</p>
              <p className="font-num">{row.consigned_units} un</p>
            </div>
            <div className="min-w-0 rounded-lg border border-white/10 bg-secondary/40 p-2.5">
              <p className="truncate text-[11px] text-muted-foreground">Vendido</p>
              <p className="font-num">{row.sold_by_partners} un</p>
            </div>
          </div>
          {row.partners.length > 0 && (
            <ul className="mt-2 space-y-1">
              {row.partners.map((p) => (
                <li key={p.partner_id} className="flex items-center justify-between gap-3 text-xs">
                  <span className="min-w-0 truncate text-muted-foreground">{p.partner_name}</span>
                  <span className="shrink-0 font-num">{p.units} un</span>
                </li>
              ))}
            </ul>
          )}
        </Surface>
      ))}
    </div>
  );
}

/** Produtos parados por parceiro: sem venda desde a última reposição, pelo prazo configurado. */
export function StalledProducts() {
  const [list, setList] = useState(null);

  useEffect(() => {
    api
      .get("/partners/stalled")
      .then(({ data }) => setList(data))
      .catch((e) => toast.error(apiError(e)));
  }, []);

  if (list === null) return <Loading label="Verificando produtos parados..." />;
  if (list.length === 0)
    return (
      <EmptyState
        icon={CheckCircle2}
        title="Nenhum produto parado"
        description="Todos os produtos consignados tiveram venda recente ou foram repostos há pouco tempo."
      />
    );

  return (
    <div className="space-y-4" data-testid="stalled-list">
      {list.map((row) => (
        <Surface key={row.partner_id} className="min-w-0 overflow-hidden p-5" data-testid={`stalled-partner-${row.partner_id}`}>
          <p className="font-display text-base font-medium">{row.partner_name}</p>
          <ul className="mt-3 divide-y divide-white/10">
            {row.items.map((it, idx) => (
              <li key={it.key || `${it.product_id}-${idx}`} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm">{it.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {`parado há ${it.days_idle} dias`}
                    {it.last_sale_at
                      ? ` · última venda há ${it.days_without_sale} dias`
                      : " · nunca vendeu"}
                    {it.last_entry_at ? ` · recebido há ${it.stock_age_days} dias` : ""}
                  </p>
                </div>
                <span className="shrink-0 font-num text-sm">{it.quantity} un</span>
              </li>
            ))}
          </ul>
        </Surface>
      ))}
    </div>
  );
}
